import React, { useMemo, useState } from "react";
import {
  buildFilterOptions,
  clearMerchantFilters,
  countActiveFilters,
} from "../../utility/merchantFilters";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

// Search box + collapsible advanced filter panel shared by the merchant tables.
// `merchants` is the *unfiltered* list — the dropdown options are derived from it
// so they stay in sync with whatever the API actually returned.
export default function MerchantFilterBar({
  merchants,
  filters,
  onChange,
  searchPlaceholder = "Search store, email, phone, referrer or ref code…",
  resultCount,
}) {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => buildFilterOptions(merchants || []), [merchants]);
  const activeCount = countActiveFilters(filters);

  const set = (key) => (e) => onChange({ ...filters, [key]: e.target.value });

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <i className='bi bi-search' style={S.searchIcon} />
          <input
            style={S.search}
            placeholder={searchPlaceholder}
            value={filters.search}
            onChange={set("search")}
          />
        </div>

        <button
          style={{ ...S.filterBtn, ...(open || activeCount ? S.filterBtnOn : {}) }}
          onClick={() => setOpen((v) => !v)}
        >
          <i className='bi bi-sliders' />
          Filters
          {activeCount > 0 && <span style={S.badge}>{activeCount}</span>}
          <i className={`bi bi-chevron-${open ? "up" : "down"}`} style={{ fontSize: 10 }} />
        </button>

        {activeCount > 0 && (
          <button
            style={S.clearBtn}
            onClick={() => onChange(clearMerchantFilters(filters))}
          >
            <i className='bi bi-x-lg' style={{ fontSize: 10 }} /> Clear all
          </button>
        )}

        {typeof resultCount === "number" && (
          <span style={S.resultCount}>{resultCount} result{resultCount === 1 ? "" : "s"}</span>
        )}
      </div>

      {open && (
        <div style={S.panel}>
          <div style={S.grid}>
            <Field label='Referred by'>
              <select style={S.select} value={filters.referrer} onChange={set("referrer")}>
                <option value='all'>Anyone</option>
                {options.noReferrerCount > 0 && (
                  <option value='none'>No referrer ({options.noReferrerCount})</option>
                )}
                {options.referrers.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                    {r.code && r.label !== r.code ? ` (${r.code})` : ""} — {r.count}
                  </option>
                ))}
              </select>
            </Field>

            <Field label='Plan'>
              <select style={S.select} value={filters.plan} onChange={set("plan")}>
                <option value='all'>Any plan</option>
                <option value='free'>Free / no plan</option>
                {options.plans.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>

            <Field label='Subscription'>
              <select
                style={S.select}
                value={filters.subscription}
                onChange={set("subscription")}
              >
                <option value='all'>Any status</option>
                <option value='active'>Active</option>
                <option value='expired'>Expired</option>
                <option value='none'>Never subscribed</option>
              </select>
            </Field>

            {options.states.length > 0 && (
              <Field label='State'>
                <select style={S.select} value={filters.state} onChange={set("state")}>
                  <option value='all'>Any state</option>
                  {options.states.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label='Login activity'>
              <select
                style={S.select}
                value={filters.loginActivity}
                onChange={set("loginActivity")}
              >
                <option value='all'>Any activity</option>
                <option value='7'>Active in last 7 days</option>
                <option value='30'>Active in last 30 days</option>
                <option value='inactive30'>Inactive 30+ days</option>
                <option value='inactive90'>Inactive 90+ days</option>
                <option value='never'>Never logged in</option>
              </select>
            </Field>

            <Field label='Joined between'>
              <div style={S.range}>
                <input
                  type='date'
                  style={S.date}
                  value={filters.joinedFrom}
                  max={filters.joinedTo || undefined}
                  onChange={set("joinedFrom")}
                />
                <span style={S.dash}>–</span>
                <input
                  type='date'
                  style={S.date}
                  value={filters.joinedTo}
                  min={filters.joinedFrom || undefined}
                  onChange={set("joinedTo")}
                />
              </div>
            </Field>

            <Field label='Last logged in between'>
              <div style={S.range}>
                <input
                  type='date'
                  style={S.date}
                  value={filters.lastLoginFrom}
                  max={filters.lastLoginTo || undefined}
                  onChange={set("lastLoginFrom")}
                />
                <span style={S.dash}>–</span>
                <input
                  type='date'
                  style={S.date}
                  value={filters.lastLoginTo}
                  min={filters.lastLoginFrom || undefined}
                  onChange={set("lastLoginTo")}
                />
              </div>
            </Field>

            <Field label='Sort by'>
              <div style={S.range}>
                <select style={S.select} value={filters.sortBy} onChange={set("sortBy")}>
                  <option value='createdAt'>Joined</option>
                  <option value='lastLogin'>Last login</option>
                  <option value='storeName'>Store name</option>
                  <option value='plan'>Plan</option>
                </select>
                <select
                  style={{ ...S.select, maxWidth: 120 }}
                  value={filters.sortDir}
                  onChange={set("sortDir")}
                >
                  <option value='desc'>Newest / Z–A</option>
                  <option value='asc'>Oldest / A–Z</option>
                </select>
              </div>
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={S.field}>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

const S = {
  wrap: { marginBottom: 18 },
  toolbar: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  searchWrap: { position: "relative", flex: 1, minWidth: 220 },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#bbb",
    fontSize: 14,
  },
  search: {
    width: "100%",
    padding: "9px 12px 9px 36px",
    borderRadius: 8,
    border: "1.5px solid #eee",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  },
  filterBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    padding: "9px 14px",
    borderRadius: 8,
    border: "1.5px solid #e0e0e0",
    background: "#fff",
    color: "#666",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  filterBtnOn: {
    borderColor: PRIMARY,
    color: "#5a8a00",
    background: "rgba(167,204,72,0.10)",
  },
  badge: {
    background: PRIMARY,
    color: "#fff",
    borderRadius: 10,
    padding: "1px 7px",
    fontSize: 10,
    fontWeight: 700,
  },
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "9px 12px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    color: "#e74c3c",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  resultCount: { fontSize: 12, color: "#999", marginLeft: "auto" },
  panel: {
    marginTop: 12,
    background: "#fff",
    border: "1.5px solid #eee",
    borderRadius: 10,
    padding: 16,
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(215px, 1fr))",
    gap: 14,
  },
  field: { display: "flex", flexDirection: "column", gap: 5, minWidth: 0 },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: BG,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  select: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 7,
    border: "1.5px solid #eee",
    fontSize: 12,
    color: "#555",
    background: "#fff",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 0,
  },
  range: { display: "flex", alignItems: "center", gap: 6, minWidth: 0 },
  date: {
    flex: 1,
    padding: "7px 8px",
    borderRadius: 7,
    border: "1.5px solid #eee",
    fontSize: 12,
    color: "#555",
    outline: "none",
    boxSizing: "border-box",
    minWidth: 0,
  },
  dash: { color: "#bbb", fontSize: 12 },
};
